import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { QueryFunction, QueryKey, useQueries } from 'react-query';

import { ICliJobQuery } from 'cli/types';
import { DEFAULT_CHECKER_INTERVAL } from 'core/constants';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { INotificationContent } from 'core/types';

interface IProps<T> {
  queries: ICliJobQuery[];
  fetchData: (requestPayload: ICliJobQuery['requestPayload'], signal: AbortSignal) => Promise<T>;
  isFetchDataFinished: (data: T) => boolean;
  onSuccess: (data?: T) => void;
  options?: {
    success?: INotificationContent;
    loading?: INotificationContent;
    error?: INotificationContent;
    notificationPosition?: NotificationPosition;
  };
  onSettled?: (data?: T) => void;
  onError?: (data?: T) => void;
  isFetchDataFailed?: (data: T) => boolean;
  getFetchDataFailedErrorMessage?: (data: T) => string;
}

interface IRefetchInterval {
  [jobId: number]: number | null;
}

export const useQueriesChecker = <
  T extends { record?: { id: number }; success?: boolean; error?: string },
>({
  queries,
  fetchData,
  isFetchDataFinished,
  onSuccess,
  options = {},
  onSettled = null,
  onError = null,
  isFetchDataFailed = null,
  getFetchDataFailedErrorMessage = null,
}: IProps<T>) => {
  const [refetchIntervals, setRefetchIntervals] = useState<IRefetchInterval>(null);
  const { clearNotification, showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const notificationPosition = options?.notificationPosition || NotificationPosition.DRAWER;

  useEffect(() => {
    if (queries.length === 0) {
      return;
    }

    setRefetchIntervals(
      queries.reduce((acc, query) => {
        acc[query.requestPayload.job] = DEFAULT_CHECKER_INTERVAL;

        return acc;
      }, {})
    );
  }, [queries]);

  const handleOnSettled = (data?: T) => {
    onSettled?.(data);

    if (isFetchDataFailed && isFetchDataFailed(data)) {
      showErrorNotification(
        { ...options?.error, message: getFetchDataFailedErrorMessage?.(data) },
        notificationPosition
      );

      setRefetchIntervals((prevValues) => ({
        ...prevValues,
        [data?.record?.id]: null,
      }));
      onError && onError(data);

      return;
    }
  };

  useQueries(
    queries.map((query) => {
      return {
        queryKey: [query.queryKey, query.requestPayload],
        queryFn: (({ signal }) => fetchData(query.requestPayload, signal)) as QueryFunction<
          T,
          QueryKey
        >,
        enabled: query.enabled,
        refetchInterval: refetchIntervals?.[query.requestPayload.job] || null,
        onSuccess: (data) => {
          if (!data?.success) {
            showErrorNotification(
              { ...options?.error, message: data?.error },
              notificationPosition
            );

            setRefetchIntervals((prevValues) => ({
              ...prevValues,
              [data?.record?.id]: null,
            }));
            onError && onError(data);

            return;
          }

          if (!isFetchDataFinished(data)) {
            return;
          }

          if (options?.success) {
            showSuccessNotification(options?.success, notificationPosition);
          } else if (options?.loading) {
            clearNotification();
          }

          setRefetchIntervals((prevValues) => ({
            ...prevValues,
            [data?.record?.id]: null,
          }));

          onSuccess(data);
        },
        onSettled: handleOnSettled,
        onError: (error?: AxiosError) => {
          showErrorNotification(options?.error, notificationPosition, error);

          setRefetchIntervals((prevValues) => ({
            ...prevValues,
            [query.requestPayload.job]: null,
          }));

          onError &&
            onError({
              record: { id: query.requestPayload.job },
              error: error?.message,
              success: false,
            } as T);
        },
      };
    })
  );
};
